import typeOf from './typeOf.js';
import _array from './types/_array.js';
import _object from './types/_object.js';
import _string from './types/_string.js';
import _boolean from './types/_boolean.js';
import types from './types/index.js';

const splicePath = function (path, idx) {
  if (path !== '') {
    return path + '.' + idx;
  } else {
    return idx;
  }
};

const isFunction = function (value) {
  return typeOf(value) === 'function';
};

const isNotFunction = function (value) {
  return typeOf(value) !== 'function';
};

/**
 * 校验调度类，使用schema对象对数据结构进行校验，没有具体的校验规则，校验规则均存放在schema对象内
 */
export default class Validator {
  constructor(schema, transforms = {}) {
    this.schema = schema;
    // 全局默认的基础类型值的转换方法
    this.transforms = {
      // type=string的值，默认去掉前后空白
      string: function ({ value }) {
        if (_string.is(value)) {
          return value.trim();
        }

        return value;
      },
      // type=number的值，如果是字符串，默认转换类型，处理用户通过表单输入字符串数字的场景
      number: function ({ value }) {
        if (_string.is(value)) {
          const val = value.trim();
          // 空字符串不转换，因为转换会变成0
          if (val !== '') {
            return Number(val);
          }

          return val;
        }

        return value;
      }
    };
    // 允许覆盖全局的转换方法
    for (let key in transforms) {
      this.transforms[key] = transforms[key];
    }

    this._error = null;
    this._success = null;
  }
  /**
   * 某个值某一个规则校验不通过时触发
   * @param {*} callback 
   */
  onError(callback) {
    if (isNotFunction(callback)) {
      throw new Error('onError参数必须是函数');
    }

    this._error = callback;
  }
  /**
   * 某个值所有规则校验通过时触发
   * @param {*} callback 
   */
  onSuccess(callback) {
    if (isNotFunction(callback)) {
      throw new Error('onSuccess参数必须是函数');
    }

    this._success = callback;
  }
  /**
   * 使用全局以及schema的转换方法来转换原始数据
   * @param {Array,Object} originData 
   */
  _transform(originData) {
    // 使用JSON做数据的深拷贝
    const newData = JSON.parse(JSON.stringify(originData));

    this._transformBySchema({
      schema: this.schema, 
      data: newData,
      value: newData, 
      parent: null, 
      idx: '', 
      path: '',
    });

    return newData;
  }
  /**
   * 递归的执行schema内的transform方法，无需中断，但会校验schema的格式
   */
  _transformBySchema({ data, schema, value, parent, idx, path }) {
    if (_object.isNot(schema)) {
      throw new Error('path=' + path + '的schema必须是对象');
    }

    const type = schema.type;

    if (type === _object.type || type === _array.type) {
      this._inspectTypeIsSame(value, type, path);

      if (type === _object.type) {
        const properties = schema.properties;
        const required = schema.required || [];

        for (let i = 0, ilen = required.length; i < ilen; i++) {
          const property = required[i];

          if (_string.isNot(property)) {
            throw new Error('属性' + property + '必须是string');
          }

          if (!(property in value)) {
            throw new Error('属性' + property + '是必须的。path=' + splicePath(path, property));
          }
        }

        if (_object.isNot(properties)) {
          throw new Error('schema属性properties的值必须是对象');
        }

        if (_array.isNot(required)) {
          throw new Error('schema属性required的值必须是数组');
        }

        for (let property in properties) {
          if (property in value) {
            this._transformBySchema({
              schema: properties[property], 
              data: data,
              value: value[property], 
              parent: value, 
              idx: property,
              path: splicePath(path, property)
            });
          }
        }
      } else if (type === _array.type) {
        if (isNotFunction(schema.items) && _object.isNot(schema.items)) {
          throw new Error('schema.items必须是对象或函数');
        }

        for (let i = 0, ilen = value.length; i < ilen; i++) {
          let iSchema = schema.items;

          if (isFunction(iSchema)) {
            iSchema = iSchema.call(null, {
              data: data,
              value: value[i], 
              parent: value, 
              idx: i,
              path: splicePath(path, i)
            });
          }

          this._transformBySchema({
            schema: iSchema, 
            data: data,
            value: value[i], 
            parent: value, 
            idx: i,
            path: splicePath(path, i)
          });
        }
      }
    } else {
      // 数组和对象外的基本值类型，执行转换
      let val = value;
      // 优先执行schema内定义的转换，再进行全局定义的转换
      if (isFunction(schema.transform)) {
        val = schema.transform.call(null, { value: value });
      } else {
        const transform = this.transforms[type];

        if (isFunction(transform)) {
          val = transform.call(this, { value: value });
        }
      }

      parent[idx] = val;
    }
  }

  _validate({ data, mode, condition, pathErrors }) {
    return this._validateBySchema({ 
      data: data,
      value: data, 
      parent: null, 
      idx: '', 
      path: '', 
      schema: this.schema, 
      mode: mode,
      condition: condition,
      pathErrors: pathErrors, 
    });
  }
  /**
   * 检查值的类型和定义类型是否相同，不相同直接抛错
   * 排除值是空字符串的情况，因为其他基础类型的值，可能允许值为空字符串
   * @param {*} value 
   * @param {*} type 
   * @param {*} path 
   */
  _inspectTypeIsSame(value, type, path) {
    if (value !== '' && typeOf(value) !== type) {
      throw new Error('类型错误：' + path + '=' + value + '，值类型为' + typeOf(value) + '，定义类型为' + type);
    }
  }
  /**
   * 递归的遍历schema，执行rules，无需中断，但会校验schema的格式
   */
  _validateBySchema({ schema, data, value, parent, idx, path, mode, condition, pathErrors }) {
    const rules = schema.rules;
    const type = schema.type;

    this._inspectTypeIsSame(value, type, path);
    // 校验值对应的规则
    let bool = this._executeRules({
      type: type,
      rules: rules, 
      data: data,
      value: value, 
      parent: parent, 
      idx: idx, 
      path: path, 
      condition: condition,
      pathErrors: pathErrors, 
    });

    if (bool) {
      if (type === _array.type) {
        for (let i = 0, ilen = value.length; i < ilen; i++) {
          let iSchema = schema.items;

          if (isFunction(iSchema)) {
            iSchema = iSchema.call(null, {
              data: data,
              value: value[i], 
              parent: value, 
              idx: i,
              path: splicePath(path, i)
            });
          }

          const subBool = this._validateBySchema({
            schema: iSchema, 
            data: data,
            value: value[i],
            parent: value,
            idx: i,
            path: splicePath(path, i),
            mode: mode,
            condition: condition,
            pathErrors: pathErrors, 
          });

          if (bool && !subBool) {
            bool = subBool;
          }

          if (!subBool && mode === 'stop') {
            break;
          }
        }
      } else if (type === _object.type) {
        const properties = schema.properties;

        for (let key in properties) {
          if (key in value) {
            const subBool = this._validateBySchema({
              schema: properties[key],
              data: data,
              value: value[key],
              parent: value,
              idx: key,
              path: splicePath(path, key),
              mode: mode,
              condition: condition,
              pathErrors: pathErrors, 
            });
  
            if (bool && !subBool) {
              bool = subBool;
            }
  
            if (!subBool && mode === 'stop') {
              break;
            }
          }
        }
      }
    }

    return bool;
  }
  /**
   * 执行rules校验，有一个rule不通过即停止执行其他的rule
   */
  _executeRules({ type, rules = [], data, value, parent, idx, path, condition, pathErrors }) {
    const params = { data, value, parent, idx, path, condition };
    let bool = true;

    if (_array.isNot(rules)) {
      throw new Error('schema.rules必须是array');
    }

    for (let i = 0, ilen = rules.length; i < ilen; i++) {
      bool = this._executeRule({ 
        ...params,
        type: type,
        rule: rules[i], 
        pathErrors: pathErrors,
      });

      if (!bool) {
        return bool;
      }
    }

    if (rules.length > 0 && bool && path && this._success) {
      this._success(params);
    }

    return bool;
  }
  /**
   * 递归的校验，同一层的rules，并且填充校验不通过的信息。所有的rules之间可以选择stop、continue模式
   */
  _executeRule({ type, rule = {}, data, value, parent, idx, path, condition, pathErrors }) {
    const params = { data, value, parent, idx, path, condition, isNotEmpty: types[type].isNotEmpty };
    let premised = true;
    let passed = true;
    let msg = '';

    if (_object.isNot(rule)) {
      throw new Error('schema.rules数组元素必须是object');
    }

    const premise = rule.premise;
    
    if (isFunction(premise)) {
      premised = premise.call(null, params);

      if (_boolean.isNot(premised)) {
        const string = premise.name !== 'premise' ? premise.name : premise.toString();
        throw new Error('rule.premise函数属性' + string + '必须返回boolean值');
      }
    }

    const validate = rule.validate;

    if (premised && isFunction(validate)) {
      const result = validate.call(null, params);

      if (_array.isNot(result)) {
        const string = validate.name !== 'validate' ? validate.name : validate.toString();
        throw new Error('rule.validate函数属性' + string + '必须返回array值');
      }

      passed = result[0];

      if (_boolean.isNot(passed)) {
        const string = validate.name !== 'validate' ? validate.name : validate.toString();
        throw new Error('rule.validate函数属性' + string + '返回数组的第一个值必须是boolean值');
      }

      msg = result[1];

      if (_string.isNot(msg)) {
        const string = validate.name !== 'validate' ? validate.name : validate.toString();
        throw new Error('rule.validate函数属性' + string + '返回数组的第二个值必须是string值');
      }
    }

    if (!passed) {
      pathErrors[path] = msg;

      if (this._error) {
        params.msg = msg;
        this._error(params);
      }
    }

    return passed;
  }
  _addErrorMsg(rootErrors, rootData, path, msg) {
    const paths = path.split('.');
    const len = paths.length;
    let errors = rootErrors;
    let data = rootData;
    let i = 0;

    while (i < len) {
      const idx = paths[i];

      data = data[idx];

      if (i === len - 1) {
        // 最后一个idx，赋值错误信息
        errors[idx] = msg;
      } else {
        if (_object.is(data)) {
          errors[idx] || (errors[idx] = {});
          errors = errors[idx];
        } else if (_array.is(data)) {
          errors[idx] || (errors[idx] = []);
          errors = errors[idx];
        }
      }

      i++;
    }
  }
  /**
   * 
   * @param {Object} param0
   * @param {[Object,Array]} param0.originData 需要校验的数据结构
   * @param {String} param0.mode 必传校验模式参数，默认=stop，oneOf(stop=碰到错误就停止, continue=校验完所有path)
   * @param {any} [param0.condition] 可选的条件，会传给rule对象的validate方法，不同语言等场景可以使用
   * @return {Promise} 校验是否通过，不通过时回传一个结构与数据结构对应的错误信息对象
   */
  run({ originData, mode, condition }) {
    if (_object.is(originData) || _array.is(originData) ) {
      const rootData = this._transform(originData);
      const pathErrors = {};

      const bool = this._validate({ 
        data: rootData, 
        mode: mode, 
        condition: condition,
        pathErrors: pathErrors
      });

      if (!bool) {
        // console.log(JSON.stringify(pathErrors, null, '  '));
        const paths = Object.keys(pathErrors);
        // 根对象校验报错，直接返回报错信息字符串，如校验根对象不能为空
        if (paths.length === 1 && paths[0] === '') {
          return Promise.reject(pathErrors[paths[0]]);
        }

        const rootErrors = this.schema.type === _object.type ? {} : [];

        for (let path in pathErrors) {
          this._addErrorMsg(rootErrors, rootData, path, pathErrors[path]);
        }

        return Promise.reject(rootErrors);
      } else {
        return Promise.resolve(rootData);
      }
    } else {
      throw new Error('originData必须是object或array');
    }
  }
}
